/**
 * Owner: Masa Bayu
 * File: toolbar-state.ts
 * Date:Time: 2026-10-05 18:12
 * Description: PoC WS-3 GREEN-A · computeToolbarState from SelectionSnapshot.
 */

import type { ToolbarCommand, ToolbarState } from './types';

export type FormatFlag = boolean | 'partial';

export interface SelectionFormatSnapshot {
  readonly bold: FormatFlag;
  readonly italic: FormatFlag;
  readonly underline: FormatFlag;
  readonly strikethrough: FormatFlag;
  readonly superscript: FormatFlag;
  readonly subscript: FormatFlag;
}

export interface SelectionStyleSnapshot {
  readonly fontFamily: string | null;
  readonly fontSize: number | null;
  readonly color: string | null;
}

export type ParagraphFormatSnapshot =
  | 'left'
  | 'center'
  | 'right'
  | 'justify'
  | null;

export interface SelectionSnapshot {
  readonly hasSelection: boolean;
  readonly selectionFormat: SelectionFormatSnapshot;
  readonly selectionStyle: SelectionStyleSnapshot | null;
  readonly paragraphFormat: ParagraphFormatSnapshot;
}

const FORMAT_COMMANDS: Array<{
  cmd: ToolbarCommand;
  key: keyof SelectionFormatSnapshot;
}> = [
  { cmd: 'bold', key: 'bold' },
  { cmd: 'italic', key: 'italic' },
  { cmd: 'underline', key: 'underline' },
  { cmd: 'strikethrough', key: 'strikethrough' },
  { cmd: 'superscript', key: 'superscript' },
  { cmd: 'subscript', key: 'subscript' },
];

const ALIGN_COMMANDS: Array<{
  cmd: ToolbarCommand;
  format: Exclude<ParagraphFormatSnapshot, null>;
}> = [
  { cmd: 'align-left', format: 'left' },
  { cmd: 'align-center', format: 'center' },
  { cmd: 'align-right', format: 'right' },
  { cmd: 'align-justify', format: 'justify' },
];

const SELECTION_BOUND_COMMANDS: ToolbarCommand[] = [
  ...FORMAT_COMMANDS.map((f) => f.cmd),
  ...ALIGN_COMMANDS.map((a) => a.cmd),
];

export function computeToolbarState(snapshot: SelectionSnapshot): ToolbarState {
  // Peraturan 1: tiada selection → semua command bound-selection disabled
  if (!snapshot.hasSelection) {
    return {
      activeCommands: [],
      disabledCommands: [...SELECTION_BOUND_COMMANDS],
      currentFontFamily: null,
      currentFontSize: null,
      currentColor: null,
    };
  }

  const active: ToolbarCommand[] = [];

  // Peraturan 2: format flags — true=active, false=enabled-not-active, partial=indeterminate
  for (const { cmd, key } of FORMAT_COMMANDS) {
    const flag = snapshot.selectionFormat[key];
    if (flag === true) active.push(cmd);
    // false dan 'partial' sengaja tidak masuk disabled (butang kekal boleh diklik)
  }

  // Peraturan 3: alignment aktif mengikut format perenggan semasa
  for (const { cmd, format } of ALIGN_COMMANDS) {
    if (snapshot.paragraphFormat === format) active.push(cmd);
  }

  return {
    activeCommands: active,
    disabledCommands: [],
    currentFontFamily: snapshot.selectionStyle?.fontFamily ?? null,
    currentFontSize: snapshot.selectionStyle?.fontSize ?? null,
    currentColor: snapshot.selectionStyle?.color ?? null,
  };
}
